import React, { useState, useEffect } from "react";
import MaterialTable from "@material-table/core";
import { ExportCsv } from "@material-table/exporters";
import { format } from "date-fns";

/*Import API helpers */
import { fetchGET } from "../apiHelper";
import { seznamRevizija } from "../listOfApi";

/*import Material-icons*/
import Search from "@material-ui/icons/Search";

/*React-bootstrap components */
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";

import RoleGuard from "../auth/RoleGuard";
import { MODULES, ROLES } from "../auth/roles";
import AccessDenied from "./ui/AccessDenied";

const Revision = () => {
  const [revision, setRevision] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Helper function to format ISO date strings
  const formatDateValue = (value) => {
    // Check if it's an ISO date string (e.g., "2025-05-15T00:00:00.000Z")
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
    if (isoDateRegex.test(value)) {
      // Extract just the date part (YYYY-MM-DD)
      return value.substring(0, 10);
    }
    return value;
  };

  // Helper function to check if string contains HTML tags
  const containsHTML = (str) => {
    const htmlRegex = /<\/?[a-z][\s\S]*>/i;
    return htmlRegex.test(str);
  };

  // Helper function to decode HTML entities
  const decodeHTMLEntities = (text) => {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;
    return textarea.value;
  };

  // Helper function to clean field value
  const cleanFieldValue = (fieldValue) => {
    let cleaned = fieldValue.trim();

    // Remove surrounding quotes (single or double)
    if (
      (cleaned.startsWith("'") && cleaned.endsWith("'")) ||
      (cleaned.startsWith('"') && cleaned.endsWith('"'))
    ) {
      cleaned = cleaned.slice(1, -1);
    }

    // Decode HTML entities
    cleaned = decodeHTMLEntities(cleaned);

    return cleaned;
  };

  // Smart split that doesn't split on '::' inside quotes or HTML tags
  const smartSplit = (str) => {
    const fields = [];
    let currentField = "";
    let inQuotes = false;
    let quoteChar = null;
    let tagDepth = 0;

    for (let i = 0; i < str.length; i++) {
      const char = str[i];
      const nextChar = str[i + 1];
      const prevChar = str[i - 1];

      // Track quotes
      if ((char === '"' || char === "'") && prevChar !== "\\") {
        if (!inQuotes) {
          inQuotes = true;
          quoteChar = char;
        } else if (char === quoteChar) {
          inQuotes = false;
          quoteChar = null;
        }
      }

      // Track HTML tag depth
      if (!inQuotes) {
        if (char === "<" && nextChar !== " ") {
          tagDepth++;
        } else if (char === ">" && prevChar !== " " && tagDepth > 0) {
          tagDepth--;
        }
      }

      // Check for separator '::'
      if (!inQuotes && tagDepth === 0 && char === ":" && nextChar === ":") {
        // Found separator outside quotes and HTML tags
        if (currentField.trim()) {
          fields.push(currentField.trim());
        }
        currentField = "";
        i += 1; // Skip '::'
        continue;
      }

      currentField += char;
    }

    // Add the last field
    if (currentField.trim()) {
      fields.push(currentField.trim());
    }

    return fields;
  };

  // Helper function to normalize HTML (remove line breaks and extra spaces)
  // Helper function to normalize HTML (remove line breaks and extra spaces)
  const normalizeHTML = (html) => {
    return html
      .replace(/\n/g, "")
      .replace(/\s+/g, " ")
      .replace(/&nbsp(?!;)/g, "&nbsp;") // fix invalid entities
      .trim();
  };

  // Helper function to detect HTML tags
  const containsHTMLTag = (str) => {
    return /<\/?[a-z][\s\S]*>/i.test(str);
  };

  // Helper function to parse and format changes
  const formatChanges = (vrednost) => {
    if (!vrednost) {
      return null;
    }

    // Check if it contains the separator '::'
    if (!vrednost.includes("::")) {
      // Simple value without separator (like "Vstop v aplikacijo")
      return <div>{vrednost}</div>;
    }

    // Debug: log the raw value

    // Use matchAll to find all field patterns: FieldName: 'value' :: 'value'
    // Use lazy matching (.*?) to handle quotes inside HTML
    const fieldPattern = /([^:]+?):\s*'(.*?)'\s*::\s*'(.*?)'(?:;|$)/gs;
    const matches = [...vrednost.matchAll(fieldPattern)];

    if (matches.length === 0) {
      // Fallback: try to match the entire string as one field
      const singleMatch = vrednost.match(
        /^([^:]+?):\s*['"](.*)['"][\s\r\n]*::[\s\r\n]*['"](.*)['"]$/s,
      );

      if (!singleMatch) {
        return (
          <div
            style={{
              padding: "10px",
              backgroundColor: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "4px",
              whiteSpace: "pre-wrap",
            }}
          >
            <strong>Neuspešno parsiranje:</strong>
            <br />
            {vrednost}
          </div>
        );
      }

      // Handle as single match
      const fieldName = singleMatch[1].trim();
      const isHTMLField = fieldName.toLowerCase() === "opis";
      let oldValue = isHTMLField
        ? normalizeHTML(singleMatch[2])
        : singleMatch[2];
      let newValue = isHTMLField
        ? normalizeHTML(singleMatch[3])
        : singleMatch[3];

      return (
        <div
          style={{
            marginBottom: "15px",
            paddingBottom: "15px",
            paddingTop: "10px",
          }}
        >
          <div style={{ marginBottom: "10px" }}>
            <strong style={{ fontSize: "15px", color: "#495057" }}>
              {fieldName}
            </strong>
          </div>

          <div style={{ marginBottom: "15px" }}>
            <div style={{ marginBottom: "5px" }}>
              <b style={{ color: "#dc3545", fontSize: "13px" }}>Staro:</b>
            </div>
            {isHTMLField ? (
              <div
                dangerouslySetInnerHTML={{ __html: oldValue }}
                style={{
                  padding: "12px",
                  backgroundColor: "#fff3f3",
                  border: "2px solid #f5c6cb",
                  borderRadius: "6px",
                  minHeight: "50px",
                  lineHeight: "1.6",
                }}
              />
            ) : (
              <div
                style={{
                  padding: "8px 12px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "4px",
                }}
              >
                {oldValue}
              </div>
            )}
          </div>

          <div>
            <div style={{ marginBottom: "5px" }}>
              <b style={{ color: "#28a745", fontSize: "13px" }}>Novo:</b>
            </div>
            {isHTMLField ? (
              <div
                dangerouslySetInnerHTML={{ __html: newValue }}
                style={{
                  padding: "12px",
                  backgroundColor: "#f1f9f1",
                  border: "2px solid #c3e6cb",
                  borderRadius: "6px",
                  minHeight: "50px",
                  lineHeight: "1.6",
                }}
              />
            ) : (
              <div
                style={{
                  padding: "8px 12px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "4px",
                }}
              >
                {newValue}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div>
        {matches.map((match, index) => {
          const fieldName = match[1].trim();
          const isHTMLField = fieldName.toLowerCase() === "opis";
          let oldValue = match[2];
          let newValue = match[3];

          // Only normalize HTML for Opis field
          if (isHTMLField) {
            oldValue = normalizeHTML(oldValue);
            newValue = normalizeHTML(newValue);
          }

          return (
            <div
              key={index}
              style={{
                marginBottom: "15px",
                borderBottom:
                  index < matches.length - 1 ? "1px solid #dee2e6" : "none",
                paddingBottom: "15px",
                paddingTop: "10px",
              }}
            >
              <div style={{ marginBottom: "10px" }}>
                <strong style={{ fontSize: "15px", color: "#495057" }}>
                  {fieldName}
                </strong>
              </div>

              <div style={{ marginBottom: "15px" }}>
                <div style={{ marginBottom: "5px" }}>
                  <b style={{ color: "#dc3545", fontSize: "13px" }}>Staro:</b>
                </div>
                {isHTMLField ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: oldValue }}
                    style={{
                      padding: "12px",
                      backgroundColor: "#fff3f3",
                      border: "2px solid #f5c6cb",
                      borderRadius: "6px",
                      minHeight: "50px",
                      lineHeight: "1.6",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      padding: "8px 12px",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "4px",
                      color: "#6c757d",
                    }}
                  >
                    {oldValue}
                  </div>
                )}
              </div>

              <div>
                <div style={{ marginBottom: "5px" }}>
                  <b style={{ color: "#28a745", fontSize: "13px" }}>Novo:</b>
                </div>
                {isHTMLField ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: newValue }}
                    style={{
                      padding: "12px",
                      backgroundColor: "#f1f9f1",
                      border: "2px solid #c3e6cb",
                      borderRadius: "6px",
                      minHeight: "50px",
                      lineHeight: "1.6",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      padding: "8px 12px",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "4px",
                      color: "#6c757d",
                    }}
                  >
                    {newValue}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const columns = [
    {
      title: "Čas spremembe",
      field: "timestamp",
      render: (rowData) => {
        const date = new Date(rowData.timestamp);
        const result = format(date, "d. M. yyyy HH:mm");
        return result;
      },
    },
    { title: "Uporabnik", field: "upn" },
    {
      title: "Akcija",
      field: "akcija",
      lookup: {
        Vstavljanje: "Vstavljanje",
        Dodajanje: "Dodajanje",
        Spremembe: "Spremembe",
        Brisanje: "Brisanje",
      },
    },
    { title: "Sklop", field: "objekt" },
    { title: "Objekt ID", field: "objektID", filtering: false },
  ];

  useEffect(() => {
    fetchGET(`${seznamRevizija}/1000`)
      .then((revision) => {
        if (revision !== 0 && Array.isArray(revision)) {
          setDateTime(revision);
        } else {
          setRevision([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching revision data:", error);
        setRevision([]);
      });
  }, []);

  const handleRevisionAllData = () => {
    fetchGET(`${seznamRevizija}/0`)
      .then((revision) => {
        if (revision !== 0 && Array.isArray(revision)) {
          setDateTime(revision);
        } else {
          setRevision([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching all revision data:", error);
        setRevision([]);
      });
  };

  const setDateTime = (revision) => {
    const revisionSorted = revision.map((item) => {
      return {
        id: item.id,
        timestamp: item.timestamp,
        upn: item.upn,
        akcija: item.akcija,
        objekt: item.objekt,
        objektID: item.objektID,
        vrednost: item.vrednost,
      };
    });
    // Sort by timestamp descending (newest first)
    revisionSorted.sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
    );
    setRevision(revisionSorted);
  };

  return (
    <RoleGuard
      moduleKey={MODULES.REVIZIJSKA_SLED}
      roles={[ROLES.GLOBALNI_ADMINISTRATOR]}
      fallback={<AccessDenied />}
    >
      <Container fluid>
        <Row>
          <Col className="main-header font-weight-bold">Zgodovina sprememb</Col>
        </Row>
        <Row>
          <Col className="header-divider">
            <hr />
          </Col>
        </Row>
        <Row>
          <Col>
            <Button
              variant="outline-primary"
              className="programs-button-add-text"
              onClick={handleRevisionAllData}
            >
              Prikaži vso zgodovino sprememb
            </Button>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col>
            <MaterialTable
              icons={{
                Filter: () => <Search style={{ fontSize: "15px" }} />,
              }}
              title={""}
              columns={columns}
              data={revision}
              localization={{
                toolbar: {
                  searchPlaceholder: "Iskalnik",
                  exportCSVName: "Izvozi podatke v CSV",
                },
                body: {
                  emptyDataSourceMessage: "Ni zapisov za prikaz",
                },
                pagination: {
                  labelRowsSelect: "Vrstic",
                  firstAriaLabel: "Prva stran",
                  firstTooltip: "Prva stran",
                  previousAriaLabel: "Prejšnja stran",
                  previousTooltip: "Prejšnja stran",
                  nextAriaLabel: "Naslednja stran",
                  nextTooltip: "Naslednja stran",
                  lastAriaLabel: "Zadnja stran",
                  lastTooltip: "Zadnja stran",
                  labelDisplayedRows: "{from}-{to} od {count}",
                },
              }}
              options={{
                headerStyle: {
                  fontWeight: "bold",
                  backgroundColor: "rgb(249, 249, 249)",
                  color: "#333",
                },
                rowStyle: {
                  fontSize: "14px",
                },
                paging: true,
                pageSize: 50,
                pageSizeOptions: [50, 100, 500, revision.length],
                searchFieldAlignment: "left",
                filtering: showFilters,
                search: true,
                exportButton: true,
                exportMenu: [
                  {
                    label: "Izvozi podatke v CSV",
                    exportFunc: (cols, datas) =>
                      ExportCsv(
                        cols,
                        datas,
                        `zgodovina_sprememb_${new Date()
                          .toLocaleDateString("sl-SI")
                          .replace(/\. /g, "-")}`,
                      ),
                  },
                ],
              }}
              onRowClick={(event, rowData, togglePanel) => togglePanel()}
              detailPanel={[
                {
                  tooltip: "Prikaži spremembo",
                  render: (rowData) => {
                    // MaterialTable might nest the data
                    const actualData = rowData.rowData || rowData;
                    const vrednostValue = actualData.vrednost;

                    if (!vrednostValue) {
                      return (
                        <div
                          style={{
                            padding: "20px",
                            backgroundColor: "#f8f9fa",
                          }}
                        >
                          <strong>
                            Ni podatkov o spremembi (vrednost is empty/null)
                          </strong>
                          <div style={{ marginTop: "10px", fontSize: "12px" }}>
                            <pre>{JSON.stringify(rowData, null, 2)}</pre>
                          </div>
                        </div>
                      );
                    }

                    const formattedChanges = formatChanges(vrednostValue);

                    return (
                      <div
                        style={{ padding: "20px", backgroundColor: "#f8f9fa" }}
                      >
                        <strong
                          style={{
                            marginBottom: "10px",
                            display: "block",
                            fontSize: "16px",
                          }}
                        >
                          Sprememba:
                        </strong>
                        <div style={{ marginTop: "10px" }}>
                          {formattedChanges || (
                            <div>formattedChanges returned null/undefined</div>
                          )}
                        </div>
                      </div>
                    );
                  },
                },
              ]}
              actions={[
                {
                  icon: "filter_alt",
                  tooltip: "Omogoči filter",
                  isFreeAction: true,
                  onClick: (event) => {
                    setShowFilters(!showFilters);
                  },
                },
              ]}
            />
          </Col>
        </Row>
      </Container>
    </RoleGuard>
  );
};

export default Revision;
